import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../app/Auth';
import { ErrorNote, Spinner } from '../app/ui';
import { getToken } from '../lib/api';
import { authCall } from '../lib/authCall';
import { acceptsCodeFromLink, approveDevice, denyDevice, describeDevice, loadDeviceInfo, normalizeUserCode, parseDeviceQuery } from '../lib/deviceApprove';
import type { DeviceInfo } from '../lib/deviceApprove';
import { isHandoffSession } from '../lib/handoff';
import { assertPasskey } from '../lib/passkey';
import { ApproveSignIns, MatchNumber } from './ApproveSignIns';

type Phase =
  | { k: 'loading' }
  | { k: 'entry'; error?: string; note?: string }
  | { k: 'ready'; info: DeviceInfo; fromLink: boolean; error?: string; errorCode?: string; busy?: boolean }
  | { k: 'approved'; info: DeviceInfo; redirecting: boolean }
  | { k: 'denied' };

const pendingOrError = (i: DeviceInfo): string | null =>
  i.status === 'pending' ? null : i.status === 'expired' ? 'This code has expired.' : 'This code was already used.';

/**
 * /device?code=XXXX-XXXX[&return=<uri>] — approve a device code with your passkey (O1/O3, Erwin 2026-10-29; NEXUS-1436).
 * Rendered whenever this browser holds tokens (lib/gate.ts, H1). A code from the link is used directly only for a
 * hand-off step-up (bound to this account); a sign-in code must be typed from the device (M1). Only the server-echoed
 * `returnUri` is ever navigated to.
 * /approve is an alias (CONTRACT_O1_NUMBER_MATCH): the "Approve sign-ins" list shows the pending hand-off step-ups with
 * the 2-digit number the glasses show — no QR to scan, no code to type.
 */
export function DevicePage() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { status, passkeyDone, signOut } = useAuth();
  const query = useMemo(() => parseDeviceQuery(loc.search), [loc.search]);
  const handoffWindow = useMemo(() => isHandoffSession(getToken()), []);
  const [phase, setPhase] = useState<Phase>({ k: 'loading' });
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (handoffWindow) return;
    if (!query.code) { setPhase({ k: 'entry' }); return; }
    let live = true;
    void loadDeviceInfo(authCall, query.code).then((r) => {
      if (!live) return;
      if (!r.ok) { setPhase({ k: 'entry', error: r.message }); return; }
      const bad = pendingOrError(r.info);
      if (bad) setPhase({ k: 'entry', error: bad });
      else if (acceptsCodeFromLink(r.info)) setPhase({ k: 'ready', info: r.info, fromLink: true });
      else setPhase({ k: 'entry', note: 'For your safety, type the code shown on the device you are holding.' });
    });
    return () => { live = false; };
  }, [query.code, handoffWindow]);

  async function lookUp(e: FormEvent) {
    e.preventDefault();
    const code = normalizeUserCode(typed);
    if (!code) { setPhase({ k: 'entry', error: 'Type the 8-character code shown on the Beam Pro screen or the device.' }); return; }
    setPhase({ k: 'loading' });
    const r = await loadDeviceInfo(authCall, code);
    if (!r.ok) { setPhase({ k: 'entry', error: r.message }); return; }
    const bad = pendingOrError(r.info);
    if (bad) setPhase({ k: 'entry', error: bad }); else setPhase({ k: 'ready', info: r.info, fromLink: false });
  }
  async function approve(info: DeviceInfo, fromLink: boolean, withReturn: boolean) {
    setPhase({ k: 'ready', info, fromLink, busy: true });
    const r = await approveDevice(info, fromLink && withReturn ? query.returnUri : null, { passkey: assertPasskey, call: authCall });
    if (!r.ok) { setPhase({ k: 'ready', info, fromLink, error: r.message, errorCode: r.code }); return; }
    setPhase({ k: 'approved', info, redirecting: !!r.redirect });
    if (r.redirect) window.location.href = r.redirect;   // server-echoed, allow-listed value only
  }
  async function deny(info: DeviceInfo, fromLink: boolean) {
    setPhase({ k: 'ready', info, fromLink, busy: true });
    const r = await denyDevice(info.userCode, authCall);
    if (r.ok) setPhase({ k: 'denied' }); else setPhase({ k: 'ready', info, fromLink, error: r.message });
  }
  async function toHelm() {
    navigate('/waiting', { replace: true });
    if (status === 'passkey') await passkeyDone();
  }

  let body;
  if (handoffWindow) {
    // L1: a hand-off window can neither use a passkey nor approve codes.
    body = <p>Approve this on your phone or Mac: open Helm there and enter the code.</p>;
  } else if (phase.k === 'loading') body = <Spinner label="Looking up the code…" />;
  else if (phase.k === 'entry') {
    body = (
      <>
      <ApproveSignIns />
      <form onSubmit={lookUp}>
        {phase.note ? <p className="muted">{phase.note}</p> : null}
        <ErrorNote text={phase.error} />
        <label htmlFor="dev-code">Code from the Beam Pro screen (or the device asking)</label>
        <input id="dev-code" autoComplete="off" autoCapitalize="characters" spellCheck={false} placeholder="XXXX-XXXX"
          value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        <button className="btn primary" type="submit" disabled={!typed.trim()}>Continue</button>
      </form>
      </>
    );
  } else if (phase.k === 'denied') body = <p>Declined. The device was told no.</p>;
  else if (phase.k === 'approved') {
    body = phase.redirecting ? <p>Done — returning to Helm XR…</p>
      : <p>Approved. {phase.info.purpose === 'handoff_stepup' ? 'The Helm XR window continues by itself.' : 'The device signs in by itself.'}</p>;
  } else {
    const { info, fromLink } = phase;
    const d = describeDevice(info);
    body = (
      <>
        {info.purpose === 'handoff_stepup' ? <MatchNumber n={info.matchNumber} /> : null}
        <p><strong>{d.title}</strong></p>
        {d.warning ? <div className="error-note" role="alert">{d.warning}</div> : null}
        {d.lines.map((l) => <p key={l} className="muted small">{l}</p>)}
        {info.purpose === 'handoff_stepup' && info.matchNumber !== null ? null
          : <p className="muted small">Code: <span className="mono">{info.userCode}</span> — check it matches what the device shows.</p>}
        <ErrorNote text={phase.error} />
        <button className="btn primary" type="button" disabled={phase.busy} onClick={() => void approve(info, fromLink, true)}>{phase.busy ? 'Waiting…' : 'Approve with passkey'}</button>
        {phase.errorCode === 'RETURN_URI_NOT_ALLOWED'
          ? <button className="btn" type="button" disabled={phase.busy} onClick={() => void approve(info, fromLink, false)}>Approve without returning</button>
          : null}
        <button className="btn" type="button" disabled={phase.busy} onClick={() => void deny(info, fromLink)}>Deny</button>
      </>
    );
  }
  return (
    <div className="center-screen">
      <div className="card signin">
        <h1>Approve sign-ins</h1>
        {body}
        <button className="btn" type="button" onClick={() => void toHelm()}>Go to Helm</button>
        <button className="btn" type="button" onClick={signOut}>Sign out</button>
      </div>
    </div>
  );
}
